// Angular
import { Component, computed, signal } from '@angular/core';

// EZ
import { DocMeta } from '@template-ui/decorators';
import { setProp } from '@template-ui/helpers';
import { EzBannerConfig, EzBannerService, EzBannerType, EzBannerTypeValue } from '@template-ui/services';
import { ButtonClickEvent, EzFormInputType, FormValuesObject, InputConfig } from '@template-ui/types';

import { EzBannerComponent } from '../../../ez-banner/ez-banner.component';
import { EzFormComponent } from '../../../ez-form/ez-form.component';
import { EzPanelComponent } from '../../../ez-panel/panel/panel.component';

type BannerDemoFormValues = FormValuesObject & {
  canClose: boolean;
  message: string;
};

@DocMeta({
  title: 'Banner Manual',
  hide: true,
  category: 'Components',
  description: 'Demonstrates explicit/manual usage of EzBannerService with EzBannerComponent.',
  showcaseTitle: 'Banner Manual',
  showcaseDesc: `
    This sample demonstrates explicit/manual usage of EzBannerService to display messages via EzBannerComponent for a specific bannerId.
    - Use the form inputs to set the close behavior and banner message.
    - Use the form buttons to trigger different banner types or clear the banner.

    ## This is not necessary if you simply want a banner on an ez-form or ez-panel. In those cases just set the [bannerMessage] input and optionally the [bannerConfig].
  `,
  showcaseComponentNames: ['EzBannerManualSampleComponent']
})
@Component({
  selector: 'ui-banner-manual-sample, ez-banner-manual-sample',
  standalone: true,
  imports: [EzBannerComponent, EzFormComponent, EzPanelComponent],
  templateUrl: './ez-banner-demo.component.html',
  styleUrls: ['./ez-banner-demo.component.scss']
})
export class EzBannerManualSampleComponent {
  /** Stable id used by this manual sample when publishing/reading banner state. */
  private readonly bannerId = 'banner-demo';

  /** Current banner visual style for this sample. */
  private readonly bannerType = signal<EzBannerTypeValue>(EzBannerType.REMINDER);

  /** Default messages keyed by banner type. */
  private readonly defaultMessages: Record<EzBannerTypeValue, string> = {
    info: 'This is an <strong>info</strong> banner.',
    warning: 'This is a <strong>warning</strong> banner.',
    error: 'This is an <strong>error</strong> banner.',
    reminder: 'This is a <strong>reminder</strong> banner.'
  };

  /** Reactive config bound to the manual banner component. */
  protected readonly bannerConfig = computed<EzBannerConfig>(() => ({
    bannerId: this.bannerId,
    canClose: Boolean(this.formValues().canClose),
    bannerType: this.bannerType()
  }));

  /** Form schema used to drive sample controls. */
  protected readonly bannerFormConfig: InputConfig[] = [
    {
      type: EzFormInputType.CHECKBOX,
      name: 'canClose',
      label: 'Show close button',
      row: 1,
      width: '1x'
    },
    {
      type: EzFormInputType.TEXTAREA,
      name: 'message',
      label: 'Message (HTML supported)',
      row: 2,
      width: 'fill',
      textAreaRows: 3
    },
    {
      type: EzFormInputType.BUTTON,
      name: 'showInfo',
      buttonText: 'Show Info',
      row: 3,
      width: '1x'
    },
    {
      type: EzFormInputType.BUTTON,
      name: 'showWarning',
      buttonText: 'Show Warning',
      row: 3,
      width: '1x'
    },
    {
      type: EzFormInputType.BUTTON,
      name: 'showError',
      buttonText: 'Show Error',
      row: 3,
      width: '1x'
    },
    {
      type: EzFormInputType.BUTTON,
      name: 'showReminder',
      buttonText: 'Show Reminder',
      row: 3,
      width: '1x'
    },
    {
      type: EzFormInputType.BUTTON,
      name: 'clear',
      buttonText: 'Clear',
      row: 3,
      width: '1x'
    }
  ];

  protected formValues = signal<BannerDemoFormValues>({
    canClose: false,
    message: this.defaultMessages.reminder,
  });

  /** Effective banner message used for message binding. */
  protected readonly bannerMessage = computed(() => {
    const bannerType = this.bannerType();
    const defaultMessage = this.defaultMessages[bannerType];
    return (this.formValues().message ?? '').toString().trim() || defaultMessage;
  });

  constructor(private bannerService: EzBannerService) {
    // One-time binding: keep banner config in sync with this demo's local config signal.
    // The service manages the reactive subscription so this component does not need a config effect.
    // This is not normally needed for a static configuration but shown here as a demonstration of
    // a reactive configuration.
    this.bannerService.setConfigSignal(this.bannerId, this.bannerConfig);

    // One-time binding: treat message as data and stream it from the computed signal.
    // This keeps message updates declarative (form -> computed -> service -> banner component).
    this.bannerService.setMessageSignal(this.bannerId, this.bannerMessage);
  }

  /** Handles sample action buttons and updates the banner state accordingly. */
  protected onFormButtonClick(event: ButtonClickEvent): void {
    switch (event.name) {
      case 'showInfo':
        this.bannerType.set(EzBannerType.INFO);
        setProp(this.formValues, 'message', this.defaultMessages.info);
        break;
      case 'showWarning':
        this.bannerType.set(EzBannerType.WARNING);
        setProp(this.formValues, 'message', this.defaultMessages.warning);
        break;
      case 'showError':
        this.bannerType.set(EzBannerType.ERROR);
        setProp(this.formValues, 'message', this.defaultMessages.error);
        break;
      case 'showReminder': {
        this.bannerType.set(EzBannerType.REMINDER);
        // Demo alternate pattern: set message directly via the service.
        const reminderMessage = this.defaultMessages.reminder + ' (set directly from EzBannerService)';
        this.bannerService.setBannerMessage(this.bannerId, reminderMessage);
        // Keep form-driven binding aligned so the signal-backed message stream does not overwrite it.
        setProp(this.formValues, 'message', reminderMessage);
        break;
      }
      case 'clear':
        this.clearBanner();
        break;
    }
  }

  /** Clears the current banner message for this sample banner id. */
  protected clearBanner(): void {
    this.bannerService.clearBannerMessage(this.bannerId);
  }
}
